/* eslint-disable react/display-name */
/* eslint-disable react/prop-types */
import React, { useContext, useState, useEffect, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { useTheme, Paper, TablePagination, Theme } from '@material-ui/core'
import MaterialTable, { Column, MTableHeader } from 'material-table'
import FormatLineSpacingIcon from '@material-ui/icons/FormatLineSpacing'

import * as LRS from '../api/LRS'
import { isDebug } from '../App.config'

import Session from '../context/SessionContext'
import StudentContext from '../context/StudentContext'

import { PrimaryHeader } from '../styles/Styles'
import { useLocalStorage } from '../utils/StorageHandler'

import { Student } from '../interfaces/ModuleTypes'
import CriticalError from './CriticalError'
import LoadingIndicator from './LoadingIndicator'
import { msToDuration } from '../Reporting'

interface Row {
    userId: string
    name: string
    llTime: number
    complete: number
    tsTime: number
    faults: number
    safety: number
    accuracy: number
    efficiency: number
}

const StudentList: React.FC = () => {
    const session = useContext(Session)
    const { setCurrentStudent } = useContext(StudentContext)
    const [summary, setSummary] = useState<LRS.ModuleSummaryResponse[]>()
    const [tableData, setTableData] = useState<Row[]>([])
    const [density, setDensity] = useLocalStorage('tableDensity', 'default')
    const [error, setError] = useState('')
    const theme = useTheme()
    const history = useHistory()

    const tableColumns: Array<Column<Row>> = getColumns(theme)

    const fetchData = useCallback(async () => {
        isDebug && console.log('Fetching data...')
        const data = await LRS.getModuleSummaryByUser(session.user.companyId)
            .catch(error => setError(error))
        if (data) setSummary(data)
    }, [session.user.companyId])

    const refreshData = () => {
        isDebug && console.log('Refreshing data...')
        setTableData([])
        setSummary(undefined)
    }

    useEffect(() => {
        if (summary) return
        fetchData()
    }, [summary, fetchData])

    useEffect(() => {
        if (tableData.length || !summary) return

        const rows: Row[] = summary.map((student: LRS.ModuleSummaryResponse, index) => {
            const studentData: Row = {
                userId: student._id,
                name: student.userName?.replace(/,/g, ', '),
                llTime: student.learningLabsTotalTime,
                complete: student.learningLabsPercentComplete
                    ? Math.round(student.learningLabsPercentComplete)
                    : 0,
                tsTime: student.troubleshootingSimsTotalTime,
                faults: student.troubleshootingSimsTotalFaultsSolved
                    ? student.troubleshootingSimsTotalFaultsSolved
                    : 0,
                safety: student.troubleshootingSimsOverallSafety
                    ? Math.round(student.troubleshootingSimsOverallSafety * 100)
                    : 0,
                accuracy: student.troubleshootingSimsOverallAccuracy
                    ? Math.round(student.troubleshootingSimsOverallAccuracy * 100)
                    : 0,
                efficiency: student.troubleshootingSimsOverallEfficiency
                    ? Math.round(student.troubleshootingSimsOverallEfficiency * 100)
                    : 0,
            }
            return studentData
        })
        setTableData(rows)

    }, [summary, tableData.length])

    const selectStudent = (event: any, rowData: Row | undefined) => {
        event.preventDefault()
        if (!rowData) return
        setCurrentStudent({} as Student)
        history.push(`/individual-data/${rowData.userId}`)
    }

    if (error) return <CriticalError message={`There was a problem loading the list of students. ${error}`} />
    const isLoading = !tableData || !summary

    return (
        <>
            <PrimaryHeader variant={'h4'}>{'Individual Data'}</PrimaryHeader>
            <MaterialTable
                title={<PrimaryHeader variant={'h4'}>{'Students'}</PrimaryHeader>}
                columns={tableColumns}
                isLoading={isLoading}
                data={tableData}
                onRowClick={(event, rowData) => selectStudent(event, rowData)}
                components={
                    {
                        Container: props => <Paper elevation={0}>{props.children}</Paper>,
                        Header: props => {
                            return (
                                <>
                                    <thead>
                                        <tr style={{
                                            color: theme.palette.secondary.main,
                                            textAlign: 'center',
                                            fontSize: theme.typography.h4.fontSize,
                                            fontFamily: 'SourceSansPro-SemiBold',
                                        }}>
                                            <td />
                                            <td colSpan={2} style={{
                                                padding: theme.spacing(3, 3, 0, 3),

                                            }}>{'Learning Labs'}
                                            </td>
                                            <td colSpan={5} style={{
                                                padding: theme.spacing(3, 3, 0, 3),

                                            }}>{'Troubleshooting Simulations'}
                                            </td>
                                        </tr>
                                    </thead>
                                    <MTableHeader {...props} />
                                </>
                            )
                        },
                        Pagination: props => {
                            return (
                                <TablePagination style={{
                                    color: theme.palette.primary.main,
                                }} {...props} />
                            )
                        }
                    }
                }
                options={{
                    loadingType: 'linear',
                    draggable: false,
                    showTitle: false,
                    search: true,
                    searchFieldStyle: {
                        color: theme.palette.primary.main,
                    },
                    searchFieldAlignment: 'left',
                    searchFieldVariant: 'standard',
                    headerStyle: {
                        fontSize: theme.typography.body1.fontSize,
                        color: theme.palette.primary.main,
                        fontFamily: 'SourceSansPro-SemiBold'
                    },
                    padding: density,
                    pageSize: 10,
                    pageSizeOptions: [5, 10, 20, 50],
                    rowStyle: {
                        color: theme.palette.primary.main,
                        fontSize: theme.typography.body1.fontSize,
                    },
                    emptyRowsWhenPaging: true,
                    toolbar: true,

                }}
                localization={{
                    pagination: {
                        labelDisplayedRows: '{from}-{to} of {count}'
                    },
                    toolbar: {
                        searchPlaceholder: 'Search by name',
                        nRowsSelected: '{0} row(s) selected'
                    },
                    header: {
                        actions: 'Actions'
                    },
                    body: {
                        emptyDataSourceMessage: isLoading ? <LoadingIndicator loading={isLoading} text={'Loading students...'} /> : 'No students to display',
                        filterRow: {
                            filterTooltip: 'Filter'
                        }
                    }
                }}
                actions={[
                    {
                        icon: () => <FormatLineSpacingIcon />,
                        tooltip: `Display density`,
                        isFreeAction: true,
                        onClick: () => setDensity(density === 'default' ? 'dense' : 'default')
                    },
                    {
                        icon: 'refresh',
                        tooltip: 'Refresh Data',
                        isFreeAction: true,
                        onClick: () => refreshData(),
                    }
                ]}
            />
        </>
    )
}

export default StudentList

const getColumns = (theme: Theme): Column<Row>[] => {
    return [
        {
            field: 'userId',
            hidden: true
        },
        {
            title: 'Name',
            field: 'name',
            defaultSort: 'asc',
        },
        {
            title: 'Time Elapsed',
            field: 'llTime',
            type: 'numeric',
            render: (rowData) => msToDuration(rowData.llTime),
            headerStyle: {
                borderLeft: `1px solid ${theme.palette.divider}`,
                whiteSpace: 'nowrap'
            },
            cellStyle: {
                borderLeft: `1px solid ${theme.palette.divider}`,
            },
            searchable: false
        },
        {
            title: 'Complete',
            field: 'complete',
            type: 'numeric',
            render: (rowData) => rowData.complete + '%',
            headerStyle: {
                borderRight: `1px solid ${theme.palette.divider}`,
                whiteSpace: 'nowrap'
            },
            cellStyle: {
                borderRight: `1px solid ${theme.palette.divider}`,
            },
            searchable: false
        },
        {
            title: 'Time Elapsed',
            field: 'tsTime',
            type: 'numeric',
            render: (rowData) => msToDuration(rowData.tsTime),
            headerStyle: {
                whiteSpace: 'nowrap'
            },
            searchable: false
        },
        {
            title: 'Faults',
            field: 'faults',
            type: 'numeric',
            searchable: false
        },
        {
            title: 'Safety',
            field: 'safety',
            type: 'numeric',
            render: (rowData) => rowData.safety + '%',
            headerStyle: {
                whiteSpace: 'nowrap'
            },
            searchable: false
        },
        {
            title: 'Accuracy',
            field: 'accuracy',
            type: 'numeric',
            render: (rowData) => rowData.accuracy + '%',
            headerStyle: {
                whiteSpace: 'nowrap'
            },
            searchable: false
        },
        {
            title: 'Efficiency',
            field: 'efficiency',
            type: 'numeric',
            render: (rowData) => rowData.efficiency + '%',
            headerStyle: {
                whiteSpace: 'nowrap'
            },
            searchable: false
        },
    ]
}

